import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  CATEGORY_LABELS,
  type Category,
  COLORS,
  CUSTOM_MAX,
  categoriesFor,
  findStyle,
  type Gender,
  type Silhouette as SilhouetteType,
  STYLES,
  type StyleRequest,
} from "../shared/styles";
import { Camera } from "./camera";
import { MemberForm } from "./home";
import { BEAUTY_LEVELS } from "./lib/beauty";
import type { Photo, Result } from "./lib/db";
import { ANGLE_LABEL, type EnrollStep, enrollPhoto } from "./lib/enroll";
import { allJobs, dismiss, enqueue, expectedMs, getJob, type Job, resultId, subscribe } from "./lib/jobs";
import { exportImage, prefetch, renderPhoto, renderResult } from "./lib/render";
import { generateMissingSamples, missingSamples, sampleUrl, useSamples } from "./lib/samples";
import { go } from "./nav";
import {
  addPhotoLocal,
  getState,
  loadPhotos,
  loadResults,
  photoUrl,
  refreshResult,
  removeMember,
  removePhoto,
  removeResult,
  saveMember,
  setBeauty,
  setColor,
  thumbUrl,
  toggleFavorite,
  useStore,
} from "./state";
import { fmtBytes, Icon, Ring, Sheet, Silhouette, toast } from "./ui";

function useJobs(): Job[] {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  return allJobs();
}

type Filter = "all" | Category;

export function Studio({ memberId, status }: { memberId: string; status: { ok: boolean } | null }) {
  const member = useStore((s) => s.members.find((m) => m.id === memberId));
  const photos = useStore((s) => s.photos[memberId]);
  const beauty = useStore((s) => s.beauty);
  const colorId = useStore((s) => s.colorId);
  const jobs = useJobs();

  const [photoId, setPhotoId] = useState<string | null>(member?.photoId ?? null);
  const [current, setCurrent] = useState<string | null>(null);
  const [focusJob, setFocusJob] = useState<string | null>(null);
  const [gender, setGender] = useState<Gender>(member?.gender ?? "female");
  const [filter, setFilter] = useState<Filter>("all");
  const [tab, setTab] = useState<"library" | "done">("library");
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [intake, setIntake] = useState<IntakeState | null>(null);
  const [camera, setCamera] = useState(false);
  const [addPhoto, setAddPhoto] = useState(false);
  const fallbackInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.body.dataset.page = "studio";
    return () => {
      delete document.body.dataset.page;
    };
  }, []);

  useEffect(() => {
    if (!member) {
      go("/");
      return;
    }
    void loadPhotos(member.id);
  }, [member?.id]);

  const photo = useMemo(() => {
    if (!photos?.length) return null;
    return photos.find((p) => p.id === photoId) ?? photos[0];
  }, [photos, photoId]);

  const results = useStore((s) => (photo ? s.results[photo.id] : undefined));
  useEffect(() => {
    if (photo) void loadResults(photo.id);
  }, [photo?.id]);

  // 任务完成 → 读回结果；如果是正在等的那个，自动切过去
  const seen = useRef(new Set<string>());
  useEffect(() => {
    for (const j of jobs) {
      if (j.stage === "done" && !seen.current.has(j.id)) {
        seen.current.add(j.id);
        void refreshResult(j.id, j.photoId).then(() => {
          if (j.id === focusJob) {
            setCurrent(j.id);
            setFocusJob(null);
          }
        });
      }
      if (j.stage === "error" && !seen.current.has(`e:${j.id}`)) {
        seen.current.add(`e:${j.id}`);
        // 正在看的那个任务，错误已经显示在照片上了，不再重复弹提示
        if (j.id !== focusJob) toast(j.error ?? "生成失败", "error");
      }
    }
  }, [jobs, focusJob]);

  const byId = useMemo(() => new Map((results ?? []).map((r) => [r.id, r])), [results]);
  // 同一发型 + 发色的最新结果（提示词升级前生成的也算，不用重新花钱）
  const byStyle = useMemo(() => {
    const m = new Map<string, Result>();
    for (const r of results ?? []) {
      if (r.styleId === "custom") continue;
      const k = `${r.styleId}|${r.colorId}`;
      const prev = m.get(k);
      if (!prev || prev.createdAt < r.createdAt) m.set(k, r);
    }
    return m;
  }, [results]);
  const findFor = (req: StyleRequest) =>
    req.styleId === "custom" ? byId.get(resultId(photo?.id ?? "", req)) : byStyle.get(`${req.styleId}|${req.colorId}`);
  const currentResult = current ? (byId.get(current) ?? null) : null;

  // 切换照片时，尽量保持同一个发型
  const switchPhoto = (p: Photo) => {
    if (p.id === photo?.id) return;
    const key = currentResult?.styleKey;
    setPhotoId(p.id);
    setCurrent(key ? `${p.id}|${key}` : null);
    setFocusJob(null);
    if (member) void saveMember({ ...member, photoId: p.id });
  };

  const request = (req: StyleRequest) => {
    if (!photo) return;
    const id = resultId(photo.id, req);
    const hit = findFor(req);
    if (hit) {
      setCurrent(hit.id);
      setFocusJob(null);
      return;
    }
    if (status && !status.ok) {
      toast("服务端还没配置生图模型的 Key", "error");
      return;
    }
    const existing = getJob(id);
    if (existing?.stage === "error") dismiss(id);
    enqueue(photo, req);
    setFocusJob(id);
  };

  const visible = useMemo(
    () => STYLES.filter((s) => s.gender === gender && (filter === "all" || s.category === filter)),
    [gender, filter],
  );

  const generateAll = () => {
    if (!photo) return;
    const todo = visible.filter(
      (s) => !findFor({ styleId: s.id, colorId }) && !getJob(resultId(photo.id, { styleId: s.id, colorId })),
    );
    if (!todo.length) {
      toast("这一页都生成过了");
      return;
    }
    if (
      todo.length > 4 &&
      !confirm(`将生成 ${todo.length} 款发型，每款约半分钟，会产生 ${todo.length} 次生图费用。继续？`)
    )
      return;
    for (const s of todo) enqueue(photo, { styleId: s.id, colorId });
    toast(`已加入 ${todo.length} 款，后台慢慢生成`);
  };

  const focused = focusJob ? jobs.find((j) => j.id === focusJob && j.stage !== "done") : undefined;
  const photoJobs = jobs.filter((j) => j.photoId === photo?.id && j.stage !== "done" && j.stage !== "error");

  if (!member) return null;

  return (
    <div class="page studio">
      <header class="topbar">
        <button class="icon-btn" aria-label="返回" onClick={() => go("/")}>
          <Icon name="back" />
        </button>
        <div class="title">
          <span>{member.name}</span>
          {photoJobs.length > 0 && <span class="pill soft">生成中 {photoJobs.length}</span>}
        </div>
        <button class="icon-btn" aria-label="更多" onClick={() => setMenu(true)}>
          <Icon name="more" />
        </button>
      </header>

      <div class={`studio-body ${photo ? "" : "no-photo"}`}>
        <div class="left">
          <div class="stage-dock">
            {photo ? (
              <Stage
                photo={photo}
                result={currentResult}
                level={beauty}
                job={focused}
                onClearJob={() => setFocusJob(null)}
                onRetry={(j) => {
                  dismiss(j.id);
                  enqueue(photo, j.req);
                  setFocusJob(j.id);
                }}
                onFavorite={currentResult ? () => void toggleFavorite(currentResult) : undefined}
                onOriginal={() => setCurrent(null)}
              />
            ) : (
              <EmptyStage
                onPick={(file) => setIntake({ file })}
                onCamera={() => setCamera(true)}
                gender={member.gender}
              />
            )}
          </div>

          <div class="tools">
            {photos && photos.length > 0 && (
              <div class="photo-strip">
                {photos.map((p) => (
                  <button class={`photo-thumb ${p.id === photo?.id ? "on" : ""}`} onClick={() => switchPhoto(p)}>
                    <img src={photoUrl(p)} alt="" />
                    <span>{ANGLE_LABEL[p.angle]}</span>
                  </button>
                ))}
                <button class="photo-thumb add" onClick={() => setAddPhoto(true)}>
                  <Icon name="plus" size={20} />
                  <span>加角度</span>
                </button>
              </div>
            )}

            {photo && (
              <div class="controls">
                <div class="control">
                  <span class="control-label">妆容</span>
                  <div class="seg small">
                    {BEAUTY_LEVELS.map((b) => (
                      <button class={beauty === b.level ? "on" : ""} onClick={() => setBeauty(b.level)} title={b.hint}>
                        {b.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div class="control">
                  <span class="control-label">发色</span>
                  <div class="swatches">
                    {COLORS.map((c) => (
                      <button
                        class={`swatch ${colorId === c.id ? "on" : ""}`}
                        onClick={() => {
                          setColor(c.id);
                          if (currentResult && currentResult.styleId !== "custom") {
                            const hit = findFor({ styleId: currentResult.styleId, colorId: c.id });
                            if (hit) setCurrent(hit.id);
                          }
                        }}
                        aria-label={c.name}
                      >
                        <i style={{ background: c.swatch }} />
                        <span>{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {photo && (
          <div class="library-col">
            <div class="lib-tabs">
              <button class={tab === "library" ? "on" : ""} onClick={() => setTab("library")}>
                发型库
              </button>
              <button class={tab === "done" ? "on" : ""} onClick={() => setTab("done")}>
                已生成 <span class="count">{results?.length ?? 0}</span>
              </button>
            </div>

            {tab === "library" ? (
              <>
                <div class="lib-filters">
                  <div class="chips">
                    {(["all", ...categoriesFor(gender)] as Filter[]).map((f) => (
                      <button class={`chip ${filter === f ? "on" : ""}`} onClick={() => setFilter(f)}>
                        {f === "all"
                          ? gender === member.gender
                            ? "全部"
                            : `全部${gender === "male" ? "男款" : "女款"}`
                          : CATEGORY_LABELS[f]}
                      </button>
                    ))}
                    <button
                      class="chip ghost-chip"
                      onClick={() => {
                        setGender(gender === "male" ? "female" : "male");
                        setFilter("all");
                      }}
                    >
                      {gender === member.gender
                        ? `也看看${gender === "male" ? "女款" : "男款"}`
                        : `回到${member.gender === "male" ? "男款" : "女款"}`}
                    </button>
                  </div>
                </div>

                <div class="style-grid">
                  {visible.map((s) => {
                    const req = { styleId: s.id, colorId };
                    const id = resultId(photo.id, req);
                    const r = findFor(req);
                    return (
                      <StyleCard
                        key={id}
                        styleId={s.id}
                        gender={s.gender}
                        name={s.name}
                        tip={s.tip}
                        silhouette={s.silhouette}
                        result={r}
                        job={getJob(id)}
                        active={(!!r && current === r.id) || focusJob === id}
                        onClick={() => request(req)}
                      />
                    );
                  })}
                </div>

                <SampleBanner gender={gender} />
                <button class="btn ghost block" onClick={generateAll}>
                  <Icon name="sparkle" size={18} /> 这一页全部生成
                </button>
                <CustomBox onSubmit={(text) => request({ styleId: "custom", colorId, custom: text })} />
              </>
            ) : (
              <DoneGrid results={results ?? []} current={current} onPick={setCurrent} />
            )}
          </div>
        )}
      </div>

      {currentResult && photos && photos.length > 1 && (
        <OtherAngles
          photos={photos.filter((p) => p.id !== photo?.id)}
          result={currentResult}
          onGenerate={(p) => {
            const req: StyleRequest = {
              styleId: currentResult.styleId,
              colorId: currentResult.colorId,
              custom: currentResult.custom,
            };
            if (getState().results[p.id]?.some((r) => r.id === resultId(p.id, req))) return;
            enqueue(p, req);
            toast(`已加入：${ANGLE_LABEL[p.angle]}`);
          }}
        />
      )}

      <Sheet open={menu} onClose={() => setMenu(false)} title={member.name}>
        <div class="menu">
          <button
            onClick={() => {
              setMenu(false);
              setEditing(true);
            }}
          >
            <Icon name="wand" size={20} /> 改名字 / 默认发型库
          </button>
          {currentResult && (
            <>
              <button
                onClick={async () => {
                  setMenu(false);
                  await saveMember({ ...member, coverResultId: currentResult.id });
                  toast("已设为首页头像");
                }}
              >
                <Icon name="heart" size={20} /> 当前发型设为头像
              </button>
              <button
                onClick={async () => {
                  setMenu(false);
                  const req = {
                    styleId: currentResult.styleId,
                    colorId: currentResult.colorId,
                    custom: currentResult.custom,
                  };
                  await removeResult(currentResult);
                  setCurrent(null);
                  if (photo) {
                    enqueue(photo, req);
                    setFocusJob(currentResult.id);
                  }
                }}
              >
                <Icon name="refresh" size={20} /> 重新生成当前发型
              </button>
            </>
          )}
          {photo && (
            <button
              class="danger"
              onClick={async () => {
                if (!confirm("删除这张照片以及它的所有发型？")) return;
                setMenu(false);
                setCurrent(null);
                await removePhoto(photo);
                setPhotoId(null);
              }}
            >
              <Icon name="trash" size={20} /> 删除当前照片
            </button>
          )}
          <button
            class="danger"
            onClick={async () => {
              if (!confirm(`删除「${member.name}」和所有照片？`)) return;
              await removeMember(member.id);
              go("/");
            }}
          >
            <Icon name="trash" size={20} /> 删除这位家人
          </button>
        </div>
      </Sheet>

      <MemberForm open={editing} onClose={() => setEditing(false)} member={member} />

      <Sheet open={addPhoto} onClose={() => setAddPhoto(false)} title="再加一张照片">
        <p class="fine">侧脸、45° 斜侧各来一张，就能从不同角度看发型。</p>
        <div class="add-photo">
          <button
            class="btn primary"
            onClick={() => {
              setAddPhoto(false);
              setCamera(true);
            }}
          >
            <Icon name="camera" size={18} /> 拍一张
          </button>
          <FilePick
            onPick={(file) => {
              setAddPhoto(false);
              setIntake({ file });
            }}
            class="btn ghost"
            label="从相册选"
          >
            <Icon name="image" size={18} /> 相册
          </FilePick>
        </div>
      </Sheet>

      {camera && (
        <Camera
          onClose={() => setCamera(false)}
          onShot={(file) => {
            setCamera(false);
            setIntake({ file });
          }}
          onFallback={() => {
            setCamera(false);
            fallbackInput.current?.click();
          }}
        />
      )}
      <input
        ref={fallbackInput}
        type="file"
        accept="image/*"
        capture="user"
        hidden
        onChange={(e) => {
          const f = (e.target as HTMLInputElement).files?.[0];
          if (f) setIntake({ file: f });
          (e.target as HTMLInputElement).value = "";
        }}
      />

      {intake && (
        <Intake
          memberId={member.id}
          file={intake.file}
          onDone={(p) => {
            setIntake(null);
            if (p) {
              addPhotoLocal(p);
              setPhotoId(p.id);
              setCurrent(null);
              void saveMember({ ...member, photoId: p.id });
            }
          }}
        />
      )}
    </div>
  );
}

// ---------------- 舞台 ----------------

const STAGE_TEXT: Record<string, string> = {
  queued: "排队中",
  models: "准备识别模型",
  upload: "上传照片",
  ai: "设计发型、描绘发丝",
  lock: "锁定五官",
  done: "完成",
  error: "没成功",
};

function Stage({
  photo,
  result,
  level,
  job,
  onClearJob,
  onRetry,
  onFavorite,
  onOriginal,
}: {
  photo: Photo;
  result: Result | null;
  level: 0 | 1 | 2;
  job?: Job;
  onClearJob: () => void;
  onRetry: (job: Job) => void;
  onFavorite?: () => void;
  onOriginal: () => void;
}) {
  const main = useRef<HTMLCanvasElement>(null);
  const orig = useRef<HTMLCanvasElement>(null);
  const [comparing, setComparing] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    const c = main.current!;
    const task = result ? renderResult(c, photo, result, level) : renderPhoto(c, photo, level);
    task.then(() => alive && setReady(true)).catch((e) => toast(String(e), "error"));
    return () => {
      alive = false;
    };
  }, [photo, result, level]);

  useEffect(() => {
    if (result) void renderPhoto(orig.current!, photo, level);
  }, [photo, result, level]);

  const style = result ? findStyle(result.styleId) : null;
  const label = result
    ? `${result.styleId === "custom" ? (result.custom ?? "自定义") : (style?.name ?? "")}${
        result.colorId !== "keep" ? ` · ${COLORS.find((c) => c.id === result.colorId)?.name ?? ""}` : ""
      }`
    : "原图";

  const save = async () => {
    const blob = await exportImage(photo, result, level);
    const name = `剪前-${label.replace(/\s|·/g, "")}.jpg`;
    const file = new File([blob], name, { type: "image/jpeg" });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        return;
      } catch {
        /* 用户取消 */
      }
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  const hold = {
    onPointerDown: () => result && setComparing(true),
    onPointerUp: () => setComparing(false),
    onPointerLeave: () => setComparing(false),
    onPointerCancel: () => setComparing(false),
    onContextMenu: (e: Event) => e.preventDefault(),
  };

  return (
    <div class={`stage ${ready ? "ready" : ""}`} style={{ aspectRatio: `${photo.width} / ${photo.height}` }} {...hold}>
      <canvas ref={main} class="stage-canvas" />
      <canvas ref={orig} class={`stage-canvas orig ${comparing ? "show" : ""}`} />

      <div class="stage-top">
        <span class="glass label">{comparing ? "原图" : label}</span>
        {result?.locked && !comparing && (
          <span class="glass lockbadge">
            <Icon name="lock" size={14} /> 五官原图
          </span>
        )}
      </div>

      {!job && (
        <div class="stage-actions" onPointerDown={(e) => e.stopPropagation()}>
          {result && (
            <button class="glass round" onClick={onOriginal} aria-label="看原图">
              <Icon name="image" size={20} />
            </button>
          )}
          {onFavorite && (
            <button class={`glass round ${result?.favorite ? "fav" : ""}`} onClick={onFavorite} aria-label="收藏">
              <Icon name="heart" size={20} filled={result?.favorite} />
            </button>
          )}
          <button class="glass round" onClick={save} aria-label="保存图片">
            <Icon name="download" size={20} />
          </button>
        </div>
      )}
      {result && !job && <div class="hold-hint">按住对比原图</div>}

      {job && <Progress job={job} onClose={onClearJob} onRetry={() => onRetry(job)} />}
    </div>
  );
}

function Progress({ job, onClose, onRetry }: { job: Job; onClose: () => void; onRetry: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.round((now - job.startedAt) / 1000));
  const exp = Math.round(expectedMs() / 1000);
  const style = findStyle(job.req.styleId);
  const steps = ["upload", "ai", "lock"] as const;
  const idx =
    job.stage === "queued" || job.stage === "models" ? -1 : steps.indexOf(job.stage as (typeof steps)[number]);

  return (
    <div class={`progress ${job.stage}`} onPointerDown={(e) => e.stopPropagation()}>
      <div class="scan" />
      <div class="progress-card">
        {job.stage === "error" ? (
          <>
            <p class="p-title">没生成出来</p>
            <p class="p-sub">{job.error}</p>
            <div class="p-actions">
              <button class="btn small ghost" onClick={onClose}>
                知道了
              </button>
              <button class="btn small primary" onClick={onRetry}>
                重试
              </button>
            </div>
          </>
        ) : (
          <>
            <div class="ring-wrap">
              <Ring value={job.progress} size={60} stroke={4.5} />
              <span class="pct">{Math.round(job.progress)}</span>
            </div>
            <p class="p-title">{style?.name ?? job.req.custom ?? "自定义发型"}</p>
            <p class="p-sub">
              {STAGE_TEXT[job.stage]}
              {job.stage === "models" && job.modelLoad
                ? ` · ${fmtBytes(job.modelLoad.loaded)} / ${fmtBytes(job.modelLoad.total)}（仅首次）`
                : ""}
            </p>
            <div class="steps">
              {["上传", "画发型", "锁五官"].map((s, i) => (
                <span class={i < idx ? "done" : i === idx ? "now" : ""}>{s}</span>
              ))}
            </div>
            <p class="p-time">
              {job.stage === "queued"
                ? "前面还有任务"
                : `已等 ${secs} 秒 · 一般 ${Math.round(exp * 0.7)}–${Math.round(exp * 1.4)} 秒`}
            </p>
            <button class="link" onClick={onClose}>
              先去看别的，后台继续
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- 发型卡片 ----------------

function StyleCard({
  styleId,
  gender,
  name,
  tip,
  silhouette,
  result,
  job,
  active,
  onClick,
}: {
  styleId: string;
  gender: Gender;
  name: string;
  tip: string;
  silhouette: SilhouetteType;
  result?: Result;
  job?: Job;
  active: boolean;
  onClick: () => void;
}) {
  useEffect(() => {
    if (result) prefetch(result);
  }, [result?.id]);
  const running = job && job.stage !== "done" && job.stage !== "error";
  const samples = useSamples();
  const hasSample = samples.ids.has(styleId);
  return (
    <button class={`style-card ${active ? "on" : ""} ${result || hasSample ? "has" : ""}`} onClick={onClick}>
      <span class="thumb">
        {result ? (
          <img src={thumbUrl(result)} alt="" loading="lazy" />
        ) : hasSample ? (
          <>
            <img src={sampleUrl(styleId)} alt="" loading="lazy" class="sample" />
            <span class="sample-tag">示例</span>
          </>
        ) : (
          <Silhouette type={silhouette} gender={gender} />
        )}
        {running && (
          <span class="thumb-busy">
            {job.stage === "queued" ? <em>排队</em> : <Ring value={job.progress} size={34} stroke={3.5} />}
          </span>
        )}
        {job?.stage === "error" && (
          <span class="thumb-err" title={job.error}>
            <Icon name="refresh" size={16} />
          </span>
        )}
        {result?.favorite && (
          <span class="thumb-fav">
            <Icon name="heart" size={12} filled />
          </span>
        )}
      </span>
      <span class="style-name">{name}</span>
      <span class="style-tip">{tip}</span>
    </button>
  );
}

/** 卡片还是插画时，提示一键换成真人示例图（一次性，所有设备共用） */
function SampleBanner({ gender }: { gender: Gender }) {
  const samples = useSamples();
  const missing = missingSamples();
  if (samples.running) {
    return (
      <p class="sample-banner">
        正在生成真人示例图 {samples.done} / {samples.total}，可以先去试发型
        {samples.failed > 0 && `（${samples.failed} 张失败）`}
      </p>
    );
  }
  if (!missing.length) return null;
  const mine = missing.filter((id) => STYLES.find((s) => s.id === id)?.gender === gender).length;
  if (!mine) return null;
  return (
    <p class="sample-banner">
      {samples.lastError ? `上次有几张没生成出来：${samples.lastError}` : "卡片上是插画示意。"}
      <button
        class="link"
        onClick={() => {
          if (
            !confirm(
              `用模型生成 ${missing.length} 张真人示例图（AI 模特，不是家人照片），约 ¥${(missing.length * 0.25).toFixed(0)}，只需一次，所有设备共用。继续？`,
            )
          )
            return;
          void generateMissingSamples();
        }}
      >
        换成真人示例图
      </button>
    </p>
  );
}

function DoneGrid({
  results,
  current,
  onPick,
}: {
  results: Result[];
  current: string | null;
  onPick: (id: string) => void;
}) {
  const sorted = [...results].sort((a, b) => Number(b.favorite) - Number(a.favorite) || b.createdAt - a.createdAt);
  if (!sorted.length) return <p class="muted empty-note">还没有生成过发型，去发型库点一个试试。</p>;
  return (
    <div class="style-grid done">
      {sorted.map((r) => {
        const s = findStyle(r.styleId);
        const color = COLORS.find((c) => c.id === r.colorId);
        return (
          <button class={`style-card has ${current === r.id ? "on" : ""}`} onClick={() => onPick(r.id)}>
            <span class="thumb">
              <img src={thumbUrl(r)} alt="" loading="lazy" />
              {r.favorite && (
                <span class="thumb-fav">
                  <Icon name="heart" size={12} filled />
                </span>
              )}
            </span>
            <span class="style-name">{r.styleId === "custom" ? (r.custom ?? "自定义") : (s?.name ?? r.styleId)}</span>
            <span class="style-tip">{color?.name ?? ""}</span>
          </button>
        );
      })}
    </div>
  );
}

function CustomBox({ onSubmit }: { onSubmit: (text: string) => void }) {
  const [text, setText] = useState("");
  return (
    <form
      class="custom"
      onSubmit={(e) => {
        e.preventDefault();
        if (!text.trim()) return;
        onSubmit(text.trim());
        setText("");
      }}
    >
      <label class="label">没找到想要的？描述一下</label>
      <div class="custom-row">
        <input
          class="field"
          placeholder="例如：齐肩外翻短发，带一点空气刘海"
          value={text}
          maxLength={CUSTOM_MAX}
          onInput={(e) => setText((e.target as HTMLInputElement).value)}
        />
        <button class="btn primary" disabled={!text.trim()}>
          生成
        </button>
      </div>
    </form>
  );
}

function OtherAngles({
  photos,
  result,
  onGenerate,
}: {
  photos: Photo[];
  result: Result;
  onGenerate: (p: Photo) => void;
}) {
  const results = useStore((s) => s.results);
  const missing = photos.filter((p) => !results[p.id]?.some((r) => r.styleKey === result.styleKey));
  if (!missing.length) return null;
  return (
    <div class="other-angles">
      <span>这款在其它角度也看看？</span>
      {missing.map((p) => (
        <button class="chip" onClick={() => onGenerate(p)}>
          生成{ANGLE_LABEL[p.angle]}
        </button>
      ))}
    </div>
  );
}

// ---------------- 录照片 ----------------

interface IntakeState {
  file: File;
}

function FilePick({
  onPick,
  children,
  class: cls,
  label,
  capture,
}: {
  onPick: (f: File) => void;
  children: ComponentChildren;
  class?: string;
  label: string;
  capture?: "user" | "environment";
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <label class={cls} aria-label={label}>
      {children}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture={capture}
        hidden
        onChange={(e) => {
          const f = (e.target as HTMLInputElement).files?.[0];
          if (f) onPick(f);
          if (ref.current) ref.current.value = "";
        }}
      />
    </label>
  );
}

function EmptyStage({ onPick, onCamera, gender }: { onPick: (f: File) => void; onCamera: () => void; gender: Gender }) {
  return (
    <div class="stage empty">
      <div class="empty-inner">
        <Silhouette type={gender === "male" ? "crop" : "lob"} gender={gender} hue="var(--sage-soft)" />
        <p class="p-title">先录一张正脸照</p>
        <p class="p-sub">正对镜头 · 光线均匀 · 露出额头</p>
        <div class="empty-actions">
          <button class="btn primary" onClick={onCamera}>
            <Icon name="camera" size={18} /> 拍一张
          </button>
          <FilePick onPick={onPick} class="btn ghost" label="从相册选">
            <Icon name="image" size={18} /> 相册
          </FilePick>
        </div>
      </div>
    </div>
  );
}

const ENROLL_TEXT: Record<EnrollStep, string> = {
  models: "准备识别模型",
  detect: "找到脸，自动裁剪",
  clean: "校正光线和白平衡",
  save: "保存",
};

function Intake({ memberId, file, onDone }: { memberId: string; file: File; onDone: (p: Photo | null) => void }) {
  const [step, setStep] = useState<EnrollStep>("models");
  const [model, setModel] = useState<{ loaded: number; total: number } | null>(null);
  const preview = useMemo(() => URL.createObjectURL(file), [file]);
  const done = useRef(false);

  const run = useCallback(async () => {
    try {
      const { photo, faceFound } = await enrollPhoto(memberId, file, setStep, (loaded, total) =>
        setModel({ loaded, total }),
      );
      if (!faceFound) toast("没认出正脸，五官锁定可能不完整。建议换一张正脸照", "error");
      else toast(`已录入：${ANGLE_LABEL[photo.angle]}`);
      onDone(photo);
    } catch (e) {
      toast(`录入失败：${(e as Error).message}`, "error");
      onDone(null);
    } finally {
      URL.revokeObjectURL(preview);
    }
  }, [memberId, file]);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    void run();
  }, [run]);

  const order: EnrollStep[] = ["models", "detect", "clean", "save"];
  return (
    <div class="intake">
      <div class="intake-card">
        <div class="intake-photo">
          <img src={preview} alt="" />
          <div class="scan" />
        </div>
        <ul class="intake-steps">
          {order.map((s) => {
            const i = order.indexOf(s);
            const cur = order.indexOf(step);
            return (
              <li class={i < cur ? "done" : i === cur ? "now" : ""}>
                <i>{i < cur ? <Icon name="check" size={14} /> : null}</i>
                {ENROLL_TEXT[s]}
                {s === "models" && step === "models" && model && model.loaded < model.total && (
                  <em>
                    {fmtBytes(model.loaded)} / {fmtBytes(model.total)}（仅首次）
                  </em>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
